const finite = (value, fallback, min, max) => Number.isFinite(Number(value)) ? Math.min(max, Math.max(min, Number(value))) : fallback;
const color = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;

export function kmlFeatureStyle(feature, maps) {
    const get = name => feature.getProperty(name);
    const style = {
        visible: get('visibility') !== false,
        strokeColor: color(get('stroke'), '#ffffff'),
        strokeOpacity: finite(get('stroke-opacity') ?? 1, 1, 0, 1),
        // CAD exports often specify subpixel widths; keep these lines visible.
        strokeWeight: finite(get('stroke-width') ?? 1, 1, 1, 32),
        fillColor: color(get('fill'), '#ffffff'),
        fillOpacity: finite(get('fill-opacity') ?? 0.6, 0.6, 0, 1),
        zIndex: 1
    };
    if (get('icon')) {
        const size = 32 * finite(get('icon-scale') ?? 1, 1, 0.1, 8);
        style.icon = { url: get('icon'), scaledSize: new maps.Size(size, size) };
        const offset = get('icon-offset'), units = get('icon-offset-units');
        if (Array.isArray(offset) && Array.isArray(units)) {
            const x = units[0] === 'fraction' ? offset[0] * size : units[0] === 'insetPixels' ? size - offset[0] : offset[0];
            const y = units[1] === 'fraction' ? offset[1] * size : units[1] === 'insetPixels' ? size - offset[1] : offset[1];
            style.icon.anchor = new maps.Point(x, size - y);
        }
    }
    return style;
}

// Unit square to quadrilateral, preserving rotated GroundOverlays and gx:LatLonQuad.
export function overlayMatrix([p0, p1, p2, p3]) {
    const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dy1 = p1.y - p2.y, dy2 = p3.y - p2.y;
    const sx = p0.x - p1.x + p2.x - p3.x, sy = p0.y - p1.y + p2.y - p3.y;
    const denominator = dx1 * dy2 - dx2 * dy1;
    if (Math.abs(denominator) < 1e-9) throw new Error('KML 影像範圍無效。');
    const g = (sx * dy2 - dx2 * sy) / denominator, h = (dx1 * sy - sx * dy1) / denominator;
    return `matrix3d(${[p1.x - p0.x + g * p1.x, p1.y - p0.y + g * p1.y, 0, g,
        p3.x - p0.x + h * p3.x, p3.y - p0.y + h * p3.y, 0, h,
        0, 0, 1, 0, p0.x, p0.y, 0, 1].join(',')})`;
}

function imageOverlay(map, feature, maps, onWarning) {
    const overlay = new maps.OverlayView(), image = document.createElement('img');
    image.alt = feature.properties.name || 'KML 底圖';
    Object.assign(image.style, { position: 'absolute', width: '1px', height: '1px', transformOrigin: '0 0', maxWidth: 'none', pointerEvents: 'none' });
    const ring = feature.geometry.coordinates[0].slice(0, 4);
    const corners = feature.bbox ? ring : [...ring].reverse();
    const protect = callback => () => { try { callback(); } catch { onWarning('KML 影像無法顯示，其他圖層仍可使用。'); } };
    overlay.onAdd = protect(() => {
        overlay.getPanes().overlayLayer.appendChild(image);
        image.onerror = () => onWarning('KML 影像載入失敗，請確認影像網址。');
        image.src = feature.properties.icon;
    });
    overlay.draw = protect(() => {
        const projection = overlay.getProjection();
        image.style.transform = overlayMatrix(corners.map(([lng, lat]) => projection.fromLatLngToDivPixel(new maps.LatLng(lat, lng))));
    });
    overlay.onRemove = () => { image.onerror = null; image.remove(); };
    overlay.setMap(map);
    return () => overlay.setMap(null);
}

export async function attachKmlMapLayer(map, geojson, { signal, fitBounds = false, onWarning = () => {} } = {}) {
    const maps = window.google.maps, disposers = [];
    let disposed = false;
    const dispose = () => {
        if (disposed) return;
        disposed = true;
        signal?.removeEventListener('abort', dispose);
        disposers.reverse().forEach(cleanup => { try { cleanup(); } catch { /* Isolate provider cleanup errors. */ } });
    };
    if (signal?.aborted) return dispose;
    signal?.addEventListener('abort', dispose, { once: true });
    try {
        const layer = new maps.Data();
        disposers.push(() => layer.setMap(null));
        layer.setStyle(feature => {
            try { return kmlFeatureStyle(feature, maps); }
            catch { onWarning('部分 KML 樣式無法顯示，已使用預設樣式。'); return {}; }
        });
        layer.setMap(map);
        const info = new maps.InfoWindow();
        disposers.push(() => info.close());
        const listener = layer.addListener('click', event => {
            try {
                const title = event.feature.getProperty('name'), description = event.feature.getProperty('description');
                if (!title && !description) return;
                const content = document.createElement('div'); content.style.maxWidth = '360px';
                const heading = document.createElement('strong'); heading.textContent = String(title || '').slice(0, 500);
                const body = document.createElement('p'); body.textContent = String(description || '').slice(0, 10000);
                content.append(heading, body); // Never execute KML description HTML.
                info.setContent(content); info.setPosition(event.latLng); info.open({ map });
            } catch { onWarning('KML 詳細資訊無法顯示，其他功能仍可使用。'); }
        });
        disposers.push(() => listener.remove());
        const vectors = [];
        for (const feature of geojson.features) {
            if (feature.properties?.['@geometry-type'] === 'groundoverlay') {
                if (feature.properties.visibility !== false && feature.properties.icon) disposers.push(imageOverlay(map, feature, maps, onWarning));
            } else vectors.push(feature);
        }
        // Yield between batches so a large CAD export cannot monopolize the UI thread.
        for (let index = 0; index < vectors.length; index += 200) {
            if (signal?.aborted) return dispose;
            layer.addGeoJson({ type: 'FeatureCollection', features: vectors.slice(index, index + 200) });
            await new Promise(resolve => setTimeout(resolve, 0));
        }
        if (fitBounds && !signal?.aborted) {
            const bounds = new maps.LatLngBounds();
            const extend = coordinates => {
                if (!Array.isArray(coordinates)) return;
                if (typeof coordinates[0] === 'number') bounds.extend({ lng: coordinates[0], lat: coordinates[1] });
                else coordinates.forEach(extend);
            };
            const geometry = value => value.type === 'GeometryCollection' ? value.geometries.forEach(geometry) : extend(value.coordinates);
            geojson.features.forEach(feature => geometry(feature.geometry));
            if (!bounds.isEmpty()) map.fitBounds(bounds);
        }
        return dispose;
    } catch (error) { dispose(); throw error; }
}
